import moment from 'moment'
import { EnumCommodity, NewPosicionPrecio, TradeDetailsResponse } from '../api'
import { FlexibilitiesModel } from '../api/Argentia/endpoints/flexibilities/model/flexibilities'
import { LeadModel } from '../api/Argentia/endpoints/leads/model/leads'
import { Operand } from '../api/Argentia/endpoints/operands/model/operand'
import { PriceCoefficientTable } from '../api/Argentia/endpoints/priceCoefficients/model/PriceCoefficient'
import { PriceCalculationTypeCompatibility } from '../api/Argentia/endpoints/pricing/model/priceType'
import { TaskTableModel } from '../api/Argentia/endpoints/tasks/model/tasks'
import { ContractModel } from '../api/Argentia/model/contracts'
import { getSubscriptionKeys } from '../config/subscriptions'
import { generateUniqueKeyWithNoPrefix, generateUniqueKeyWithTimestamp } from '../helpers'
import { AppConfigurationKey } from '../model/enums/appConfigurationKey'
import { InformationDialogs } from '../model/enums/informationDialogs'
import { User } from '../model/RBAC'
import { ClientFileModel } from '../pages/clients/clients.model'
import { ContractProduct, NewContractProduct } from '../pages/contracts/contracts.model'
import { LeadFormSearchFilter } from '../pages/leads/components/searchFilterCard/searchFilterCard'
import { LeadsSearchMode } from '../pages/leads/leads.model'
import { PriceDetails, PriceVersion } from '../pages/pricing/pricing.Model'
import { TaskFormSearchFilter } from '../pages/tasks/components/searchFilterCardTasks/searchFilterCardTasks'
import {
  notifyErrorAction,
  setIsLoadingAction,
  storeActiveBalanceZoneAction,
  storeActiveCompanyAction,
  storeActiveCommodityAction,
  storeActivePositionAction,
  storeActivePriceAction,
  storeActiveTabAction,
  storeActiveTabPowerAction,
  storeActiveTradeAction,
  storeBalanceZonesAction,
  storeCalculationTypesAction,
  storeCoefficientsAction,
  storeCommodityFilterAction,
  storeCompaniesAction,
  storeCustomFilterAction,
  storeDataTableFilterAction,
  storeDataTableRowsPerPageAction,
  storeDataTableSearchAction,
  storeDateFilterAction,
  storeLayoutAction,
  storeLogoutHandlerAction,
  storeOperandsAction,
  storeShowOnlyActivesFilterAction,
  storeUserAction,
  storeVersionsAction,
  switchReleaseNotificationAction,
  toggleSideChangelogAction,
  toggleSideHelpAction
} from './actions'
import { storeAppConfigurationAction } from './actions/appConfiguration.actions'
import {
  clearActiveContractAction,
  clearActiveFlexibilityAction,
  clearClientCommercialsAction,
  clearClientContractsAction,
  clearClientDataAction,
  fetchClientCommercialsAsync,
  fetchClientContractsAsync,
  fetchClientDataAsync,
  fetchContractAsync,
  fetchContractProductsAsync,
  fetchCountriesAsync,
  fetchMunicipalitiesAsync,
  fetchProvincesAsync,
  removeClientContractAction,
  setActiveContractAction,
  setDesactiveContractAction,
  storeActiveContractAction,
  storeActiveFlexibilityAction,
  storeActiveTabAditionalsAction,
  storeAddContractProductTempAction,
  storeClientContractAction,
  storeClientContractsAction,
  storeClientDataAction,
  storeRemoveContractProductTempAction,
  storeSearchTextAction,
  storeUpdateClientContractAction,
  storeUpdateContractProductTempAction,
  switchOffCommunicationFromAdd,
  switchOffCommunicationFromFile
} from './actions/clients.actions'
import {
  closeConfirmationDialogAction,
  configureConfirmationDialogAction,
  configureInformationDialogAction,
  openConfirmationDialogAction,
  setIsWaitingConfirmationDialogAction
} from './actions/confirmationDialog.actions'
import {
  storeLeadsDatatableAction,
  storeLeadSearchModeAction,
  storeLeadsSearchFilters
} from './actions/leads.action'
import {
  storeShowFilterCardAction,
  storeShowTasksAction,
  storeTasksDatatableAction,
  storeTasksSearchFilters
} from './actions/tasks.actions'
import { store } from './store'
import { userMapperToStore } from './store.mapper'
import {
  AppConfigurationItemModel,
  BalanceZoneStoreModel,
  CommodityFilterModel,
  CompanyStoreModel,
  ConfirmationDialogConfigurationStoreModel,
  CustomFilterModel,
  DataTableFilter,
  DateFilterModel,
  InformationDialogConfigurationStoreModel
} from './store.model'

export const notifyError = (message: string) => {
  store.dispatch(notifyErrorAction(message))
}

export const clearErrors = () => {
  store.dispatch(notifyErrorAction(''))
}

export const setIsLoading = (isLoading: boolean) => {
  store.dispatch(setIsLoadingAction(isLoading))
}

export const storeLogoutHandler = (logoutHandler: () => void) => {
  store.dispatch(storeLogoutHandlerAction(logoutHandler))
}

export const storeUser = (user: User) => {
  store.dispatch(storeUserAction(userMapperToStore(user)))
}

export const storeCompanies = (companies: CompanyStoreModel[]) => {
  store.dispatch(storeCompaniesAction(companies))
}

export const storeActiveCompany = (id: number) => {
  store.dispatch(storeActiveCompanyAction(id))
}

export const storeBalanceZones = (balanceZones: BalanceZoneStoreModel[]) => {
  store.dispatch(storeBalanceZonesAction(balanceZones))
}

export const storeActiveBalanceZone = (id: number) => {
  store.dispatch(storeActiveBalanceZoneAction(id))
}

export const storeLayout = (autoCollapse: boolean) => {
  store.dispatch(storeLayoutAction(autoCollapse))
}

export const getLayoutSideNavAutoCollapse = () => {
  return store.getState().layout.sideNavAutoCollapse
}

export const getActiveCompany = () => {
  return store.getState().profile.activeCompany
}

export const getActiveCommodities = () => {
  return store.getState().profile.activeCompany?.commodities
}

export const getActiveCommodity = (): EnumCommodity | undefined => {
  return store.getState().profile.activeCommodity
}

export const storeActiveCommodity = (commodity: EnumCommodity) => {
  store.dispatch(storeActiveCommodityAction(commodity))
}

export const getActiveCommodityFilter = () => {
  const currentPage = store.getState().router.location?.pathname
  return store.getState().pagesState.find(arrayElement => arrayElement.page === currentPage)
    ?.commodityFilter
}

export const getActiveBalanceZone = () => {
  return store.getState().profile.activeBalanceZone
}

export const getActiveUser = () => {
  return store.getState().profile.user!
}

export const getBalanceZonesForCompany = () => {
  return store.getState().profile?.balanceZones && store.getState().profile?.balanceZones
}

export const getCustomerDomain = () => {
  return store.getState().profile.user
    ? store.getState().profile.user?.domain
      ? store.getState().profile.user?.domain
      : ''
    : ''
}

export const getCustomerDomainClientArea = () => {
  return store.getState().profile.user
    ? store.getState().profile.user?.domainClientArea
      ? store.getState().profile.user?.domainClientArea
      : ''
    : ''
}

export const getCustomerDomainPrefix = () => {
  const state = store.getState()
  return state.profile.user
    ? state.profile.user?.domain
      ? state.profile?.user?.domain.indexOf('.') !== -1
        ? state.profile.user?.domain.substr(0, state.profile?.user?.domain.indexOf('.'))
        : state.profile.user?.domain
      : ''
    : ''
}

export const openConfirmationDialog = () => {
  store.dispatch(openConfirmationDialogAction())
}

export const closeConfirmationDialog = () => {
  store.dispatch(closeConfirmationDialogAction())
}

// Information Dialog (Communication)

export const openInformationDialog = (type: InformationDialogs) => {
  // check AppConfig && User Session (Redux Client State)
  const clientState = store.getState().clientsState
  const dueDate = getAppConfigurationValue(AppConfigurationKey.FrontNoticeDueDate)
  switch (type) {
    case InformationDialogs.COMMUNICATION_NEW_PAGE_CLIENT_ADD: {
      if (clientState.showCommunicationAdd && dueDate && moment(dueDate).isAfter(moment())) {
        openConfirmationDialog()
        store.dispatch(switchOffCommunicationFromAdd())
      }
      break
    }
    case InformationDialogs.COMMUNICATION_NEW_PAGE_CLIENT_FILE: {
      if (clientState.showCommunicationFile && dueDate && moment(dueDate).isAfter(moment())) {
        openConfirmationDialog()
        store.dispatch(switchOffCommunicationFromFile())
      }
      break
    }
    default:
      openConfirmationDialog()
  }
}

export const setIsWaitingConfirmationDialog = (isWaiting: boolean) => {
  store.dispatch(setIsWaitingConfirmationDialogAction(isWaiting))
}

export const configureConfirmationDialog = (
  configuration: ConfirmationDialogConfigurationStoreModel | undefined
) => {
  store.dispatch(configureConfirmationDialogAction(configuration))
}

export const configureInformationDialog = (
  configuration: InformationDialogConfigurationStoreModel | undefined
) => {
  store.dispatch(configureInformationDialogAction(configuration))
}

export const toggleSideHelp = () => {
  store.dispatch(toggleSideHelpAction())
}

export const toggleSideChangelog = () => {
  store.dispatch(toggleSideChangelogAction())
}

export const switchOffReleaseNotification = () => {
  store.dispatch(switchReleaseNotificationAction(false))
}

export const switchOnReleaseNotification = () => {
  store.dispatch(switchReleaseNotificationAction(true))
}

export const storeAppConfiguration = (items: AppConfigurationItemModel[]) => {
  const newReleaseAPIValue = items.find(
    x => x.key === AppConfigurationKey.SiteHelpNewReleaseNotification
  )
  const newReleaseStoreValue = store.getState().layout.newReleaseNotification

  const config = items.concat(getSubscriptionKeys())

  store.dispatch(storeAppConfigurationAction(config))

  if (newReleaseAPIValue && newReleaseStoreValue == null) {
    store.dispatch(switchReleaseNotificationAction(Boolean(newReleaseAPIValue.value)))
  }
}

export const getAppConfigurationValue = (key: string) => {
  return store.getState().appConfiguration.find(x => x.key === key)?.value
}

export const storeDataTableFilterForCurrentPage = (filter: DataTableFilter) => {
  const currentPage = store.getState().router.location?.pathname
  store.dispatch(storeDataTableFilterAction(currentPage, filter))
}

export const storeDataTableSearchForCurrentPage = (search: string) => {
  const currentPage = store.getState().router.location?.pathname
  store.dispatch(storeDataTableSearchAction(currentPage, search))
}

export const storeDataTableRowsPerPageForCurrentPage = (rowsPerPage: number) => {
  const currentPage = store.getState().router.location?.pathname
  store.dispatch(storeDataTableRowsPerPageAction(currentPage, rowsPerPage))
}

export const storeDateFilterForCurrentPage = (filter: DateFilterModel) => {
  const currentPage = store.getState().router.location?.pathname
  store.dispatch(storeDateFilterAction(currentPage, filter))
}

export const storeCustomFilterForCurrentPage = (filter: CustomFilterModel) => {
  const currentPage = store.getState().router.location?.pathname
  store.dispatch(storeCustomFilterAction(currentPage, filter))
}

export const storeActiveTabForPage = (tab: number, page: string) => {
  store.dispatch(storeActiveTabAction(page, tab))
}

export const storeActiveTabForCurrentPage = (tab: number) => {
  const currentPage = store.getState().router.location?.pathname
  store.dispatch(storeActiveTabAction(currentPage, tab))
}

export const storeActiveTabForClientFilePage = (
  tab: number,
  page?: 'PRICE_COMPONENTS' | 'FLEXIBILITY'
) => {
  storeActiveTabForCurrentPage(tab)
  page && store.dispatch(storeActiveTabAditionalsAction(page))
}

export const storeActiveTabPowerForCurrentPage = (tab: number) => {
  const currentPage = store.getState().router.location?.pathname
  store.dispatch(storeActiveTabPowerAction(currentPage, tab))
}

export const storeCommodityFilterForCurrentPage = (filter: CommodityFilterModel) => {
  const currentPage = store.getState().router.location?.pathname
  store.dispatch(storeCommodityFilterAction(currentPage, filter))
}

export const storeShowOnlyActivesFilterForCurrentPage = (showOnlyActives: boolean) => {
  const currentPage = store.getState().router.location?.pathname
  store.dispatch(storeShowOnlyActivesFilterAction(currentPage, showOnlyActives))
}

export const getUserName = () => {
  return store.getState().profile.user
    ? store.getState().profile.user?.name
      ? store.getState().profile.user?.name
      : ''
    : ''
}

export const getUserId = () => {
  return store.getState().profile.user?.id
}

export const storeActivePrice = (price: PriceDetails | null) => {
  store.dispatch(storeActivePriceAction(price))
}

export const getActivePrice = () => {
  return store.getState().pricingState.price
}

export const storeVersions = (versions: PriceVersion[] | null) => {
  store.dispatch(storeVersionsAction(versions))
}

export const getVersions = () => {
  return store.getState().pricingState.versions
}

export const storeOperands = (operands: Operand[]) => {
  store.dispatch(storeOperandsAction(operands))
}

export const getPersistedOperands = () => {
  return store.getState().pricingState.operands
}

export const storeCalculationTypes = (calculationTypes: PriceCalculationTypeCompatibility[]) => {
  store.dispatch(storeCalculationTypesAction(calculationTypes))
}

export const getPersistedCompatibleCalculationType = () => {
  return store.getState().pricingState.calculationTypes
}

export const getCoefficients = () => {
  return store.getState().pricingState.coefficients
}

export const storeCoefficients = (coefficients: PriceCoefficientTable[] | null) => {
  store.dispatch(storeCoefficientsAction(coefficients))
}
// Client starts

export const fetchClientContractsByID = (clientID: number) => {
  return store.dispatch<any>(fetchClientContractsAsync(clientID))
}

export const fetchClientCommercialsByID = (clientID: number) => {
  return store.dispatch<any>(fetchClientCommercialsAsync(clientID))
}

export const fetchClientDataByID = (clientID: number) => {
  return store.dispatch<any>(fetchClientDataAsync(clientID))
}

export const fetchClientContract = (contractID: number) => {
  return store.dispatch<any>(fetchContractAsync(contractID))
}

export const fetchProductsByContract = (contractID: number) => {
  return store.dispatch<any>(fetchContractProductsAsync(contractID))
}

export const fetchCountriesOptions = () => {
  return store.dispatch<any>(fetchCountriesAsync())
}

export const fetchProvincesOptions = () => {
  return store.dispatch<any>(fetchProvincesAsync())
}

export const fetchMunicipalitiesOptions = () => {
  return store.dispatch<any>(fetchMunicipalitiesAsync())
}

export const storeClientContracts = (contracts: ContractModel[]) => {
  store.dispatch(storeClientContractsAction(contracts))
}

export const storeClientContract = (contract: ContractModel) => {
  store.dispatch(storeClientContractAction(contract))
}

export const storeActiveContract = (contract: ContractModel | null) => {
  store.dispatch(storeActiveContractAction(contract))
}

export const clearActiveContract = () => {
  store.dispatch(clearActiveContractAction())
}

export const storeActiveFlexibility = (flexibility: FlexibilitiesModel | null) => {
  store.dispatch(storeActiveFlexibilityAction(flexibility))
}

export const clearActiveFlexibility = () => {
  store.dispatch(clearActiveFlexibilityAction())
}

export const storeUpdateClientContract = (contract: ContractModel) => {
  store.dispatch(storeUpdateClientContractAction(contract))
}

export const storeAddContractProductTemp = (product: NewContractProduct) => {
  store.dispatch(storeAddContractProductTempAction(product))
}

export const storeUpdateContractProductTemp = (product: NewContractProduct) => {
  store.dispatch(storeUpdateContractProductTempAction(product))
}

export const storeRemoveContractProductTemp = (tempId: string | number) => {
  store.dispatch(storeRemoveContractProductTempAction(tempId))
}

export const storeClientData = (clientData: ClientFileModel) => {
  store.dispatch(storeClientDataAction(clientData))
}

export const setActiveContract = (contractID: number) => {
  store.dispatch(setActiveContractAction(contractID))
}

export const setDesactiveContract = () => {
  store.dispatch(setDesactiveContractAction())
}

export const clearClientData = () => {
  store.dispatch(clearClientDataAction())
}

export const clearClientContracts = () => {
  store.dispatch(clearClientContractsAction())
}

export const clearClientCommercials = () => {
  store.dispatch(clearClientCommercialsAction())
}

export const removeClientContract = (contractID: number) => {
  store.dispatch(removeClientContractAction(contractID))
}

export const getClientContractsStore = (): ContractModel[] | undefined => {
  return store.getState().clientsState.clientContracts
}

export const getContractProducts = (): ContractProduct[] | undefined | null => {
  return store.getState().clientsState.contractProducts
}

export const getClientDataStore = (): ClientFileModel | undefined => {
  return store.getState().clientsState.clientData
}

export const getIsFetchingClientDataStore = (): boolean => {
  return store.getState().clientsState.isFetchingClientData
}

export const getIsFetchingClientContractsStore = (): boolean => {
  return store.getState().clientsState.isFetchingClientContracts
}

export const storeSearchText = (searchText: string) => {
  store.dispatch(storeSearchTextAction(searchText))
}

export const getSearchText = () => {
  return store.getState().clientsState.searchText
}

// Client ends

// Trades
export const storeActiveTrade = (trade: TradeDetailsResponse | null) => {
  store.dispatch(storeActiveTradeAction(trade))
}

export const getActiveTrade = () => {
  return store.getState().tradesState.trade
}

export const storeActivePosition = (position: NewPosicionPrecio | null) => {
  store.dispatch(storeActivePositionAction(position))
}

export const getActivePosition = () => {
  return store.getState().tradesState.position
}

// #region Tasks start
export const storeShowTasksState = (showTasks: boolean = false) => {
  store.dispatch(storeShowTasksAction(showTasks))
}

export const getShowTasksState = () => {
  return store.getState().tasksState.showTasks
}

export const storeShowFilterCardState = (showSearchFilter: boolean = false) => {
  store.dispatch(storeShowFilterCardAction(showSearchFilter))
}

export const getShowFilterCardState = () => {
  return store.getState().tasksState.showSearchFilter
}

export const storeTasksSearchFilterState = (filters: TaskFormSearchFilter) => {
  store.dispatch(storeTasksSearchFilters(filters))
}

export const getTasksSearchFilterState = () => {
  return store.getState().tasksState.tasksSearchFilters
}

export const storeTasksDataTable = (tasksDataTable: TaskTableModel[]) => {
  store.dispatch(storeTasksDatatableAction(tasksDataTable))
}

export const getTasksDataTable = () => {
  return store.getState().tasksState.tasksDataTable
}
// #endregion  Tasks end

// #region Leads start
export const storeLeadSearchModeState = (searchMode: LeadsSearchMode = LeadsSearchMode.None) => {
  store.dispatch(storeLeadSearchModeAction(searchMode))
}

export const getLeadSearchModeState = () => {
  return store.getState().leadsState.searchMode
}

export const storeLeadsSearchFilterState = (filters: LeadFormSearchFilter) => {
  store.dispatch(storeLeadsSearchFilters(filters))
}

export const getLeadsSearchFilterState = () => {
  return store.getState().leadsState.leadsSearchFilters
}

export const storeLeadsDataTable = (leadsDataTable: LeadModel[]) => {
  store.dispatch(storeLeadsDatatableAction(leadsDataTable))
}

export const getLeadsDataTable = () => {
  return store.getState().leadsState.leadsDataTable
}
// #endregion  Leads end

// Generic

// Add new product to temp List
export const addNewItemTempList = <T extends { tempId?: string; timeStamp?: number }>(
  item: T,
  itemList: T[],
  canEdit?: boolean
) => {
  const { tempId, timestamp } = generateUniqueKeyWithTimestamp()
  const newItem: T = {
    ...item,
    tempId: tempId,
    timeStamp: timestamp, // added to take latest changed Value
    canEdit: canEdit
  }
  return [...itemList, newItem]
}

// Update saved product or new product (not yet saved)
export const updateTempList = <T extends { id?: string | number; tempId?: string }>(
  item: T,
  list: T[],
  canEdit?: boolean
): T[] => {
  let updatedItem: T = item

  if (!updatedItem?.tempId) {
    updatedItem = {
      ...item,
      tempId: generateUniqueKeyWithNoPrefix(),
      canEdit: canEdit
    }
  }

  return list.map((l: T) => {
    if (
      (l.id && updatedItem.id && l.id == updatedItem.id) ||
      (l.tempId && updatedItem.tempId && l.tempId == updatedItem.tempId)
    ) {
      return updatedItem
    }
    return l
  })
}

// Remove from list is only used for recently added items (not saved, products without ID)
export const removeOrMarkProductTempList = <
  T extends { id?: string | number; tempId?: string; delete?: boolean }
>(
  list: T[],
  item: T,
  canEdit?: boolean
): T[] => {
  // If is a temp item, remove from list.
  // If not, mark it as to be removed
  return list
    .filter(p => !(!p.id && p.tempId === item.tempId))
    .map(p => {
      return p.id && p.id === item.id ? { ...p, delete: true, canEdit: canEdit } : p
    })
}

// id undefined
// tempId
// ==> quitar de la lista

// id
// tempId undefined
// ==> delete

// id
// tempId
// ==> delete
